import type { LDClient } from 'launchdarkly-react-client-sdk';

import { isValidBranchName } from '@/utils/branch-cookie';

/**
 * The `gaia-prototype` LaunchDarkly flag assigns users to a prototype UI branch.
 *
 * It is a JSON flag, so it is deliberately not part of FEATURE_FLAG_DEFAULTS (which is boolean-only).
 * @see docs/designs/labs-prototype-assignment.md
 */
export const PROTOTYPE_FLAG_KEY = 'gaia-prototype';

export interface PrototypeFlag {
  /** Branch to serve; empty means "no prototype". */
  branch: string;
  title?: string;
  description?: string;
}

export const PROTOTYPE_FLAG_DEFAULT: PrototypeFlag = { branch: '' };

export const PROTOTYPE_GENERIC_DESCRIPTION =
  'A preview of upcoming changes to ATLAS. You can switch back at any time.';

export interface PrototypeFlagRead {
  /** False when the value is a fallback rather than an answer from LaunchDarkly. */
  trusted: boolean;
  prototype: PrototypeFlag;
}

/**
 * Validates a flag variation. Variations are hand-edited JSON, so anything malformed is treated
 * as "no prototype".
 */
export function parsePrototypeFlag(value: unknown): PrototypeFlag {
  if (value === null || value === undefined) return PROTOTYPE_FLAG_DEFAULT;

  const invalid = () => {
    console.warn(`[Labs] Ignoring malformed ${PROTOTYPE_FLAG_KEY} variation:`, value);
    return PROTOTYPE_FLAG_DEFAULT;
  };

  if (typeof value !== 'object' || Array.isArray(value)) return invalid();

  const { branch, title, description } = value as Record<string, unknown>;
  if (typeof branch !== 'string') return invalid();
  if (title !== undefined && typeof title !== 'string') return invalid();
  if (description !== undefined && typeof description !== 'string') return invalid();
  if (branch === '') return PROTOTYPE_FLAG_DEFAULT;
  if (!isValidBranchName(branch)) return invalid();

  return { branch, title, description };
}

/**
 * Reads the flag, reporting whether the value actually came from LaunchDarkly.
 *
 * If initialization failed or timed out, the SDK evaluates to the default with an `ERROR` reason
 * (`FLAG_NOT_FOUND`); a missing client means LaunchDarkly isn't configured. Neither is a
 * trustworthy "no prototype". But `FLAG_NOT_FOUND` while LaunchDarkly delivered other flags means
 * the flag was deleted or archived, which is a trustworthy "no prototype".
 *
 * @param ldHasFlags - whether LaunchDarkly delivered any flags at all
 */
export function readPrototypeFlag(
  ldClient: LDClient | undefined,
  ldHasFlags: boolean
): PrototypeFlagRead {
  if (!ldClient) return { trusted: false, prototype: PROTOTYPE_FLAG_DEFAULT };

  const detail = ldClient.variationDetail(PROTOTYPE_FLAG_KEY, PROTOTYPE_FLAG_DEFAULT);
  if (detail.reason?.kind === 'ERROR') {
    const deleted = detail.reason.errorKind === 'FLAG_NOT_FOUND' && ldHasFlags;
    return { trusted: deleted, prototype: PROTOTYPE_FLAG_DEFAULT };
  }
  return { trusted: true, prototype: parsePrototypeFlag(detail.value) };
}

/**
 * The prototype's display title, derived from the branch name when the variation omits one:
 * `ux-1234-new-equipment-page` becomes "New equipment page".
 */
export function prototypeTitle(prototype: PrototypeFlag): string {
  if (prototype.title) return prototype.title;

  const lastSegment = prototype.branch.split('/').pop() ?? '';
  const words = lastSegment
    .replace(/^[a-z]+-\d+-?/i, '')
    .replace(/[-_]+/g, ' ')
    .trim();
  if (!words) return prototype.branch;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The prototype's description, or generic copy when the variation omits one.
 */
export function prototypeDescription(prototype: PrototypeFlag): string {
  return prototype.description || PROTOTYPE_GENERIC_DESCRIPTION;
}
